// ═══════════════════════════════════════════════════════════════════════════════
// Receipt detail — view + helper delete (when allowed)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { COMMENT_TARGET_TYPE, RECEIPT_STATUS, USER_ROLE } from '@fdh/shared';
import type { Receipt, ReceiptItem, ReceiptStatus } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { CommentThread } from '../../components/comments';
import { StatusBadge } from '../../components/task';
import { ReceiptImageLightbox } from '../../components/receipt/ReceiptImageLightbox';
import { HelperUnsettledReceiptBanner } from '../../components/settlement/HelperUnsettledReceiptBanner';
import { PreviousHelperBanner } from '../../components/pairing/PreviousHelperBanner';
import {
  AutoTranslationHint,
  receiptRemarkHasTranslation,
  resolveReceiptRemarkDisplay,
  resolveTranslatedDisplayText,
  translationLanguageNameKey,
} from '../../components/translation';
import { useToast } from '../../components/ui/ToastProvider';
import { AppButton, appButtonClassName } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { resolveApiErrorMessage } from '../../i18n/mapApiClientError';
import { budgetApi, receiptApi } from '../../lib/api';
import {
  buildSettlementsHrefForBudgets,
  matchMainBudgetsForReceiptDates,
} from '../../utils/matchBudgetsForReceiptDates';

const isDeletableStatus = (status: ReceiptStatus): boolean => status === RECEIPT_STATUS.SAVE;

const isHelperEditableReceiptStatus = (status: ReceiptStatus): boolean => status === RECEIPT_STATUS.SAVE;

const formatReceiptQtyForDetail = (qty: ReceiptItem['qty']): string =>
  qty === '' ? '—' : String(qty);

const formatReceiptLineAmountForDetail = (amount: ReceiptItem['amount']): string => {
  if (amount === '') return '—';
  return `$${amount.toFixed(2)}`;
};

const EMPLOYER_DECISION_STATUSES: ReadonlySet<ReceiptStatus> = new Set([RECEIPT_STATUS.REVIEWED]);

const shouldShowEmployerReviewSubmittedAt = (r: Receipt): boolean =>
  r.reviewedAt.trim() !== '' && EMPLOYER_DECISION_STATUSES.has(r.status);

export const ReceiptDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showLineItemsOriginal, setShowLineItemsOriginal] = useState(false);
  const [showRemarkOriginal, setShowRemarkOriginal] = useState(false);
  const [helperSettlementsHref, setHelperSettlementsHref] = useState('/settlements');

  const isHelper = user?.role === USER_ROLE.HELPER;
  const isEmployer = user?.role === USER_ROLE.EMPLOYER;
  const isFromPreviousHelper = receipt?.isFromPreviousHelper === true;
  const isEmployerViewingSavedReceipt =
    user?.role === USER_ROLE.EMPLOYER &&
    receipt?.status === RECEIPT_STATUS.SAVE &&
    !isFromPreviousHelper;
  const canDelete =
    receipt && isHelper && isDeletableStatus(receipt.status) && !isFromPreviousHelper;
  const canHelperEdit =
    Boolean(
      receipt &&
        isHelper &&
        user?.id &&
        receipt.submittedByUserId === user.id &&
        isHelperEditableReceiptStatus(receipt.status) &&
        !isFromPreviousHelper,
    );
  const mainBottomPaddingClass = useMemo(() => {
    return 'pb-28';
  }, []);

  const load = useCallback(async () => {
    if (!id) {
      setError(t('receipt.detail.loadError'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await receiptApi.get(id, language);
      if (res.success && res.data) {
        setReceipt(res.data);
      } else {
        setError(res.error ?? t('receipt.detail.loadError'));
      }
    } catch {
      setError(t('receipt.detail.loadError'));
    } finally {
      setLoading(false);
    }
  }, [id, t, language]);

  useEffect(() => {
    setShowLineItemsOriginal(false);
    setShowRemarkOriginal(false);
  }, [language]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isHelper || !receipt || receipt.status !== RECEIPT_STATUS.SAVE) {
      setHelperSettlementsHref('/settlements');
      return;
    }
    let cancelled = false;
    const loadSettleHref = async (): Promise<void> => {
      try {
        const res = await budgetApi.list('all', 1, 50, '2000-01-01', '9999-12-31');
        if (cancelled || !res.success || !res.data) {
          return;
        }
        const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        const matched = matchMainBudgetsForReceiptDates(rows, [receipt.receiptDate]).filter(
          (row) => !row.isLocked,
        );
        if (cancelled) {
          return;
        }
        setHelperSettlementsHref(buildSettlementsHrefForBudgets(matched));
      } catch {
        if (!cancelled) {
          setHelperSettlementsHref('/settlements');
        }
      }
    };
    void loadSettleHref();
    return () => {
      cancelled = true;
    };
  }, [isHelper, receipt]);

  const handleOpenDelete = () => {
    setDeleteError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (deleteLoading) return;
    setDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!receipt || !id) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await receiptApi.delete(id, receipt.version);
      if (res.success && res.data?.deleted) {
        setDialogOpen(false);
        showSuccessToast(t('toast.receipt.deleteSuccess'));
        navigate('/receipts');
      } else {
        setDeleteError(res.error ?? t('receipt.analyze.submitError'));
      }
    } catch (err) {
      setDeleteError(resolveApiErrorMessage(err, t, 'receipt.analyze.submitError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatMoney = (n: number) => (n < 0 ? `-$${Math.abs(n).toFixed(2)}` : `$${n.toFixed(2)}`);

  const firstLineItemTranslation = useMemo(
    () => (receipt?.items ?? []).find((row) => row.translation?.hasTranslation)?.translation,
    [receipt?.items],
  );

  const hasLineItemNames = useMemo(
    () => (receipt?.items ?? []).some((row) => row.item.trim() !== ''),
    [receipt?.items],
  );

  const lineItemsTargetLanguageLabel = firstLineItemTranslation?.targetLanguage
    ? t(translationLanguageNameKey(firstLineItemTranslation.targetLanguage))
    : t(translationLanguageNameKey(language));

  const remarkTrimmed = receipt?.remark?.trim() ?? '';
  const remarkHasTranslation = Boolean(
    isEmployer && receiptRemarkHasTranslation(remarkTrimmed, receipt?.translation),
  );
  const remarkTargetLanguageLabel = receipt?.translation?.targetLanguage
    ? t(translationLanguageNameKey(receipt.translation.targetLanguage))
    : t(translationLanguageNameKey(language));
  const remarkDisplay = resolveReceiptRemarkDisplay(
    remarkTrimmed,
    receipt?.translation,
    showRemarkOriginal,
  );

  const renderLineItemName = (it: ReceiptItem): ReactNode => {
    const trimmed = it.item.trim();
    if (trimmed === '') {
      return '—';
    }
    const displayName = resolveTranslatedDisplayText(
      it.item,
      it.translation?.translatedItem,
      showLineItemsOriginal,
    );
    return <span className="break-words">{displayName}</span>;
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
              aria-label={t('task.action.back')}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('receipt.detail.title')}</h1>
          </div>
        </div>
      </header>

      <main className={`mx-auto max-w-lg space-y-6 px-4 py-6 ${mainBottomPaddingClass}`}>
        {!loading && !error && isFromPreviousHelper ? (
          <PreviousHelperBanner variant="receipt" />
        ) : null}
        {!loading && !error && isHelper && receipt?.status === RECEIPT_STATUS.SAVE ? (
          <HelperUnsettledReceiptBanner settlementsHref={helperSettlementsHref} />
        ) : null}
        {!loading && !error && isEmployerViewingSavedReceipt ? (
          <div
            className="flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/90 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30"
            role="note"
            aria-live="polite"
          >
            <svg
              className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M10.29 3.86l-8 14A1 1 0 003.16 19h17.68a1 1 0 00.87-1.5l-8-14a1 1 0 00-1.74 0z"
              />
            </svg>
            <p className="text-sm leading-relaxed text-emerald-900 dark:text-emerald-100/90">
              {t('receipt.detail.employerSavedReceiptNotice')}
            </p>
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : receipt ? (
          <div className="space-y-4 rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
            {receipt.originalImageUrl && receipt.originalImageUrl.trim() !== '' && id ? (
              <div>
                <div className="mb-2 flex min-h-[1.5rem] items-center justify-between gap-3">
                  <h2 className="min-w-0 text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {t('receipt.detail.photoHeading')}
                  </h2>
                  <div className="shrink-0">
                    <StatusBadge status={receipt.status} size="sm" />
                  </div>
                </div>
                <ReceiptImageLightbox
                  receiptId={id}
                  alt={t('receipt.detail.photoAlt')}
                  enlargeAriaLabel={t('receipt.analyze.enlargeImageAria')}
                  closeLabel={t('common.closeViewer')}
                />
              </div>
            ) : (
              <div className="flex min-h-[1.5rem] justify-end">
                <StatusBadge status={receipt.status} size="sm" />
              </div>
            )}
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-gray-500 dark:text-gray-400">{t('receipt.detail.date')}</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">{receipt.receiptDate}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-gray-500 dark:text-gray-400">{t('receipt.detail.submittedAt')}</dt>
                <dd className="text-right font-medium tabular-nums text-gray-900 dark:text-gray-100">
                  {receipt.submittedAt}
                </dd>
              </div>
              {shouldShowEmployerReviewSubmittedAt(receipt) ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('receipt.detail.employerReviewSubmittedAt')}
                  </dt>
                  <dd className="text-right font-medium tabular-nums text-gray-900 dark:text-gray-100">
                    {receipt.reviewedAt}
                  </dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-2">
                <dt className="text-gray-500 dark:text-gray-400">{t('receipt.detail.total')}</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(receipt.totalAmount)}</dd>
              </div>
              {receipt.company ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500 dark:text-gray-400">{t('receipt.analyze.field.company')}</dt>
                  <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                    {receipt.company}
                  </dd>
                </div>
              ) : null}
              {remarkTrimmed ? (
                <div className="flex justify-between gap-2">
                  <dt className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <span>{t('receipt.analyze.field.remark')}</span>
                    {remarkHasTranslation ? (
                      <AutoTranslationHint
                        targetLanguageLabel={remarkTargetLanguageLabel}
                        showingOriginal={showRemarkOriginal}
                        onToggleOriginal={() => setShowRemarkOriginal((prev) => !prev)}
                      />
                    ) : null}
                  </dt>
                  <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                    {remarkDisplay}
                  </dd>
                </div>
              ) : null}
            </dl>

            {(receipt.items ?? []).length > 0 ? (
              <section aria-labelledby="receipt-detail-items-heading">
                <div className="flex items-center gap-1">
                  <h2
                    id="receipt-detail-items-heading"
                    className="text-sm font-semibold text-gray-800 dark:text-gray-200"
                  >
                    {t('receipt.analyze.items')}
                  </h2>
                  {hasLineItemNames ? (
                    <AutoTranslationHint
                      targetLanguageLabel={lineItemsTargetLanguageLabel}
                      showingOriginal={showLineItemsOriginal}
                      onToggleOriginal={() => setShowLineItemsOriginal((prev) => !prev)}
                    />
                  ) : null}
                </div>
                <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full min-w-[280px] border-collapse text-left text-sm">
                    <thead className="bg-gray-50 text-gray-600 dark:bg-gray-800/80 dark:text-gray-300">
                      <tr>
                        <th scope="col" className="px-3 py-2 font-medium">
                          {t('receipt.analyze.itemName')}
                        </th>
                        <th scope="col" className="w-20 px-3 py-2 text-right font-medium">
                          {t('receipt.analyze.qty')}
                        </th>
                        <th scope="col" className="w-28 px-3 py-2 text-right font-medium">
                          {t('receipt.analyze.amount')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(receipt.items ?? []).map((it, idx) => (
                        <tr key={`receipt-item-${idx}`} className="text-gray-900 dark:text-gray-100">
                          <td className="px-3 py-2 align-top">{renderLineItemName(it)}</td>
                          <td className="px-3 py-2 text-right align-top tabular-nums">
                            {formatReceiptQtyForDetail(it.qty)}
                          </td>
                          <td className="px-3 py-2 text-right align-top tabular-nums">
                            {formatReceiptLineAmountForDetail(it.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {canHelperEdit && id ? (
              <Link
                to={`/receipts/${encodeURIComponent(id)}/edit`}
                className={appButtonClassName({ variant: 'primary', fullWidth: true })}
                tabIndex={0}
                aria-label={t('receipt.detail.editReceipt')}
              >
                {t('receipt.detail.editReceipt')}
              </Link>
            ) : null}

            {canDelete ? (
              <AppButton
                type="button"
                variant="danger"
                fullWidth
                onClick={handleOpenDelete}
                aria-haspopup="dialog"
                aria-expanded={dialogOpen}
                aria-label={t('receipt.detail.delete')}
              >
                {t('receipt.detail.delete')}
              </AppButton>
            ) : null}

          </div>
        ) : null}

        {receipt && id ? (
          <CommentThread
            targetType={COMMENT_TARGET_TYPE.RECEIPT}
            targetId={id}
            readOnly={isFromPreviousHelper}
          />
        ) : null}
      </main>


      <ConfirmDialog
        open={dialogOpen}
        title={t('receipt.detail.deleteConfirmTitle')}
        description={t('receipt.detail.deleteConfirmBody')}
        confirmLabel={deleteLoading ? t('receipt.detail.deleting') : t('receipt.detail.delete')}
        cancelLabel={t('task.action.back')}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={handleCloseDialog}
        variant="danger"
        confirmDisabled={deleteLoading}
        cancelDisabled={deleteLoading}
      >
        {deleteError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">
            {deleteError}
          </p>
        ) : null}
      </ConfirmDialog>
    </div>
  );
};
